/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The value an earlier case tested, as the editor offers it** (`X55b`, slice `S3b`, `#1247` gap 3).
 *
 * The engine half is `MacroExpressionEngine`'s binding scope, and it carries the reasoning. This is
 * the half with a rule in it on the authoring side, kept as a pure module for the reason
 * `case-ladder.model.ts` is one: *which cases a slot can read* and *what a read writes* are the
 * feature, and a component test that clicks the chip would be asserting a rendering rather than them.
 *
 * ## Three rules, all ruled by Todd on 2026-09-29
 *
 * - **Named in the model, offered on the subject.** A case with one comparison offers its left side.
 *   A case with more is listed and **refused with its reason**, because which value it tested is
 *   ambiguous; binding either side of one is an addition to this module, not to what is stored.
 * - **Visible from its case on.** A case's tested value reaches its own outcome, every later case's
 *   condition and outcome, and *Otherwise* — never its own condition.
 * - **Written when first read, with no armed state.** Each case without a `bindAs` is given a
 *   **proposed** name. A slot writes a read of that name, and the ladder's `emit` stamps `bindAs` on
 *   any case whose proposed name the emitted ladder now reads ({@link stampReadBindings}). So the
 *   binding is derived from the document being written, never from something clicked earlier.
 */

import type { CaseLadder, CaseRung } from './case-ladder.model';
import { flattenPredicate, predicateFromList } from './condition-builder.model';
import { summarizeMacroExpression } from './expression-summary';
import { readConditionOperands, type MacroCondition } from '../../../types/decorator-expression';

/** One tested value a slot may read, or may not and says why. */
export interface TestedValue {
  /** The name a read writes: the case's stored `bindAs`, or the name proposed for it. */
  name: string;
  /** What the operator reads — where the value was tested, never the stored name. */
  label: string;
  /** What the case tests, summarised, so two cases testing different things read differently. */
  tests: string;
  /** Why this case's value cannot be read, or `null` where it can. */
  refusal: string | null;
}

/** The tested values in force at one slot, nearest case last. Empty outside any conditional. */
export type TestedScope = readonly TestedValue[];

/** Where a slot sits in a ladder. A case's condition does not see its own tested value. */
export type LadderPosition =
  | { kind: 'condition'; index: number }
  | { kind: 'then'; index: number }
  | { kind: 'otherwise' };

/**
 * **A generated name.** Never a readable word: references are inlined into their caller at the
 * config read boundary, so a shared expression's `tested1` and its caller's `tested1` would be the
 * same name at runtime. `BoundValueExpression.name` carries the rest.
 */
export function proposeBindingName(): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 10)
      : Math.random().toString(36).slice(2, 12);
  return `t_${random}`;
}

/** The one comparison a case tests, or `null` with the reason there is not exactly one. */
function soleComparison(rung: CaseRung): { condition: MacroCondition } | { refusal: 'many' | 'grouped' | 'none' } {
  const list = flattenPredicate(rung.condition);
  if (!list) return { refusal: 'grouped' };
  if (list.conditions.length !== 1) return { refusal: list.conditions.length === 0 ? 'none' : 'many' };
  return { condition: list.conditions[0] };
}

/**
 * **One case, as an entry in a slot's scope.** `label` says where it was tested; `prefix` names an
 * enclosing ladder's cases, so a nested decision's *case 1* and its container's are not one label.
 *
 * A case with more than one comparison is refused, and offering it is `#1497`: which comparison, and
 * what a short-circuit binds, are its questions.
 */
export function testedValueOf(rung: CaseRung, index: number, proposed: string, prefix = ''): TestedValue {
  const label = `${prefix}case ${index + 1}`;
  const sole = soleComparison(rung);
  if ('refusal' in sole) {
    const refusal =
      sole.refusal === 'none'
        ? `Case ${index + 1} tests nothing yet, so it has no value to reuse.`
        : `Case ${index + 1} tests more than one comparison, so there is no single value to reuse. Give it one comparison, or make the value a shared expression and address it from both places.`;
    // A case bound while it had one comparison keeps its name, so a read of it is told what changed
    // rather than that it moved.
    const kept = flattenPredicate(rung.condition)?.conditions.find((c) => c.bindAs)?.bindAs;
    return { name: kept ?? proposed, label, tests: '', refusal };
  }
  const { left } = readConditionOperands(sole.condition);
  const tests = left ? summarizeMacroExpression(left) : '';
  if (!left || tests === '' || (left.type === 'calculated' && !left.path.trim())) {
    return {
      name: sole.condition.bindAs ?? proposed,
      label,
      tests: '',
      refusal: `Case ${index + 1} tests nothing yet, so it has no value to reuse.`,
    };
  }
  return { name: sole.condition.bindAs ?? proposed, label, tests, refusal: null };
}

/**
 * **What a slot at `position` may read**: the enclosing scope, then this ladder's cases up to and
 * including its own for an outcome, before its own for a condition, and all of them for *Otherwise*.
 */
export function scopeAt(
  ladder: CaseLadder,
  position: LadderPosition,
  enclosing: TestedScope,
  proposalFor: ProposalFor,
): TestedScope {
  const last =
    position.kind === 'otherwise'
      ? ladder.rungs.length - 1
      : position.kind === 'then'
        ? position.index
        : position.index - 1;
  const prefix = enclosing.length ? 'this decision’s ' : '';
  const own = ladder.rungs
    .slice(0, last + 1)
    .map((rung, index) => testedValueOf(rung, index, proposalFor(rung), prefix));
  // Nearer names shadow farther ones, as the engine resolves them, so a stale enclosing entry with a
  // name this ladder also binds is not offered twice.
  const shadowed = new Set(own.map((entry) => entry.name));
  return [...enclosing.filter((entry) => !shadowed.has(entry.name)), ...own];
}

/**
 * **The name proposed for a case, keyed by the case's condition object — never by its position.**
 *
 * Measured on the first build: keyed by index, deleting a bound case slid the next case into its
 * position and its proposal, so the next emit **stamped the surviving case with the deleted one's
 * name** and every read of it silently began reading a different value. The condition object is the
 * case's identity for this purpose: a reorder, a delete, and an edit to the case's outcome all keep
 * it, and an edit to the condition cannot happen in the same emit as a read.
 */
export type ProposalFor = (rung: CaseRung) => string;

/** A {@link ProposalFor} over a store that outlives a render — a `WeakMap` held in a ref. */
export function proposalsIn(store: WeakMap<object, string>): ProposalFor {
  return (rung) => {
    const key = rung.condition as object;
    let name = store.get(key);
    if (!name) {
      name = proposeBindingName();
      store.set(key, name);
    }
    return name;
  };
}

/** Every `bound` read anywhere beneath `value` — the set {@link stampReadBindings} checks names against. */
export function boundReads(value: unknown, into: Set<string> = new Set()): Set<string> {
  if (Array.isArray(value)) {
    for (const entry of value) boundReads(entry, into);
  } else if (typeof value === 'object' && value !== null) {
    const node = value as Record<string, unknown>;
    if (node.type === 'bound' && typeof node.name === 'string') into.add(node.name);
    for (const entry of Object.values(node)) boundReads(entry, into);
  }
  return into;
}

/**
 * **Stamp `bindAs` on every case whose proposed name the ladder now reads** — the write half of the
 * offer, applied in the ladder's `emit`.
 *
 * Only a case with exactly one comparison and no `bindAs` is stamped, which is exactly the set
 * {@link testedValueOf} offers under a proposed name. The reads are collected from the **whole**
 * ladder being emitted, so a read in a later case, in *Otherwise* or inside a nested decision is found
 * wherever the change came from. A proposal nobody reads writes nothing.
 */
export function stampReadBindings(ladder: CaseLadder, proposalFor: ProposalFor): CaseLadder {
  const reads = boundReads(ladder);
  if (!reads.size) return ladder;
  let changed = false;
  const rungs = ladder.rungs.map((rung) => {
    const proposed = proposalFor(rung);
    if (!proposed || !reads.has(proposed)) return rung;
    const list = flattenPredicate(rung.condition);
    if (!list || list.conditions.length !== 1 || list.conditions[0].bindAs) return rung;
    changed = true;
    return {
      ...rung,
      condition: predicateFromList({ ...list, conditions: [{ ...list.conditions[0], bindAs: proposed }] }),
    };
  });
  return changed ? { ...ladder, rungs } : ladder;
}

/**
 * **Why a read cannot find its value here**, or `null` where it can. A case that changed under the
 * read says what changed; one that is not before the read any more says that. A reorder that moves a reader
 * above the case it reads is the ordinary way to get one; it is an **error**, never a block (*block
 * on an invalid value, never on a missing one*), and the engine resolves it to nothing.
 */
export function outOfScopeReason(name: string, scope: TestedScope): string | null {
  const entry = scope.find((candidate) => candidate.name === name);
  if (entry) return entry.refusal;
  return 'This reads a value tested by a case that is not before it any more, so it resolves to nothing. Move the case back above this one, or choose another tested value.';
}
